import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Info from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CopyrightIcon from '@mui/icons-material/Copyright';
import PrintIcon from '@mui/icons-material/Print';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import generateRepairPDF from '../repairandreplacement/Bill';
import '../shared.css';

const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
const getFormattedStartDate = () => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
};

function RepairAndReplacementIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [fromDate, setFromDate] = useState(getFormattedStartDate);
    const [toDate, setToDate] = useState(getFormattedCurrentDate);
    const history = useHistory();

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) fetchTableData();
    }, [fromDate, toDate, companyId, branchId]);

    const fetchTableData = async () => {
        setData([]);
        try {
            const response = await fetchData('RepairAndReplacementApi', `/CustomerDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const filtered = data.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleDelete = async () => {
        deleteData('RepairAndReplacementApi', `${deleteItemId}`, token)
            .then(() => { fetchTableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch(err => console.error('Error deleting record:', err));
    };

    const handleActivate = (itemId) => {
        activateData('RepairAndReplacementApi', `${itemId}`, token)
            .then(() => fetchTableData())
            .catch(err => console.error('Error activating record:', err));
    };

    const handlePrint = async (saleid) => {
        try {
            const response = await fetchData('RepairAndReplacementApi', `/SalePrint/${saleid}/${branchId}`, token);
            generateRepairPDF(response.response_data);
        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };

    function formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }

    return (
        <div className="pg-container">
            {/* ── Header ── */}
            <div className="pg-header">
                <h5 className="pg-title">Repair / Replacement Receipt</h5>
                <div className="pg-header-actions">
                    {/* Date filters */}
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate}
                                onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate}
                                onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} title="Refresh">
                            <RefreshIcon /> Refresh
                        </button>
                    </div>

                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                        {hasCreateMasterPermission && (
                            <Link to="/RepairAndReplacementCreate" className="pg-btn pg-btn-primary">
                                <AddIcon /> Create New
                            </Link>
                        )}
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {/* ── Table ── */}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Receipt Date</th>
                            <th>Receipt Number</th>
                            <th>Customer</th>
                            <th>Phone No</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0
                            ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filtered.map((item, index) => (
                                <tr key={item.Id} style={{ opacity: item.IsActive ? 1 : 0.5 }}>
                                    <td>{index + 1}</td>
                                    <td>{formatDate(item.BillDate)}</td>
                                    <td>{item.BillPrefix}/{item.BillNumber}/{item.FinYear}</td>
                                    <td>{item.CustomerName}</td>
                                    <td>{item.ContactNumber}</td>
                                    <td style={{ textAlign: 'right' }}>{item.TotalAmount ? item.TotalAmount.toFixed(2) : '0.00'}</td>
                                    <td className="actions-cell">
                                        <IconButton size="small" title="Print" onClick={() => handlePrint(item.Id)}>
                                            <PrintIcon style={{ color: 'teal', fontSize: 17 }} />
                                        </IconButton>
                                        <Link to={`/RepairAndReplacementDetails/${item.Id}`}>
                                            <IconButton size="small" title="Details"><Info style={{ color: 'coral', fontSize: 17 }} /></IconButton>
                                        </Link>
                                        {hasEditMasterPermission && item.IsActive && (
                                            <Link to={`/RepairAndReplacementEdit/${item.Id}`}>
                                                <IconButton size="small" color="primary" title="Edit"><EditIcon style={{ fontSize: 17 }} /></IconButton>
                                            </Link>
                                        )}
                                        {hasDeleteMasterPermission && (
                                            <IconButton size="small"
                                                title={item.IsActive ? 'Delete' : 'Activate'}
                                                onClick={() => item.IsActive
                                                    ? (setDeleteItemId(item.Id), setConfirmDialogOpen(true))
                                                    : handleActivate(item.Id)}>
                                                {item.IsActive
                                                    ? <DeleteIcon style={{ fontSize: 17, color: '#dc2626' }} />
                                                    : <ActivateIcon style={{ fontSize: 17, color: 'darkolivegreen' }} />}
                                            </IconButton>
                                        )}
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions>
                    <button className="pg-btn pg-btn-danger" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
                </DialogActions>
            </Dialog>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default RepairAndReplacementIndex;
